import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { LeadsTableRow } from '@pages/Admin/CRM/utils/mapLeadsToTableRows'
import {
  KanbanFieldFilterValue,
  KanbanState,
  KanbanCard,
  StatusId,
  KanbanColumnSort,
  KanbanGetBoardResponse,
} from './kanban.types'
import { parseRefetchKanbanColumnArg } from './kanbanRefetchColumn'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { buildPatchedKanbanCardProviderUnread } from '../lib/patchKanbanCardVkUnread'
import {
  fetchFunnelsThunk,
  fetchBoardInitThunk,
  fetchFunnelUnassignedCountsThunk,
  fetchManagersThunk,
  fetchSourcesThunk,
  fetchColumnMoreThunk,
  refetchKanbanColumnThunk,
  refetchKanbanBoardThunk,
  refreshKanbanColumnsAfterLeadChangeThunk,
} from './kanban.thunks'

const applyFunnelUnassignedCounts = (
  state: KanbanState,
  rows: { id: number; unassigned_count: number }[],
) => {
  const nextUnassigned: Record<number, number> = {}
  for (const row of rows) {
    const fid = Number(row.id)
    if (Number.isFinite(fid)) {
      nextUnassigned[fid] = Math.max(0, Number(row.unassigned_count) || 0)
    }
  }
  state.funnelUnassignedCountById = nextUnassigned
}

const applyKanbanBoardPayload = (
  state: KanbanState,
  payload: KanbanGetBoardResponse,
) => {
  const prevColumnSortByStatusId = { ...state.columnSortByStatusId }

  state.cardsById = {}
  state.columnsByStatusId = {}
  state.columnSortByStatusId = {}
  state.columnOrder = []

  payload.columns.forEach((column) => {
    const ids = column.items.map((card) => {
      state.cardsById[card.leadFunnelId] = card
      return card.leadFunnelId
    })

    const prevSort = prevColumnSortByStatusId[column.statusId]
    state.columnSortByStatusId[column.statusId] = prevSort
      ? { ...prevSort }
      : {
          isExpanded: false,
          enabled: false,
          sortField: 'next_contact_at',
          sortOrder: 'desc',
        }

    state.columnsByStatusId[column.statusId] = {
      statusId: column.statusId,
      statusName: column.statusName,
      statusColor: column.statusColor,
      ids,
      total: column.total,
      cursor: column.nextCursor,
      hasMore: column.hasMore,
      loadingInitial: false,
      loadingMore: false,
      refetchOverlay: false,
      error: null,
    }
    state.columnOrder.push(column.statusId)
  })
  state.cardSyncPendingByLeadFunnelId = {}

  applyFunnelUnassignedCounts(state, payload.funnels ?? [])
}

const initialState: KanbanState = {
  funnels: [],
  loadingFunnels: false,
  funnelsError: null,
  managersById: {},
  loadingManagers: false,
  managersError: null,
  sources: [],
  loadingSources: false,
  sourcesError: null,
  activeFunnelId: null,
  filters: {},
  cardsById: {},
  columnsByStatusId: {},
  columnSortByStatusId: {},
  columnOrder: [],
  loadingBoard: false,
  error: null,
  cardSyncPendingByLeadFunnelId: {},
  funnelUnassignedCountById: {},
}

const kanbanSlice = createSlice({
  name: 'crmKanban',
  initialState,
  reducers: {
    setActiveFunnelId(state, action: PayloadAction<number | null>) {
      if (state.activeFunnelId !== action.payload) {
        delete state.filters.fieldFilters
      }
      state.activeFunnelId = action.payload
    },
    setFilters(state, action: PayloadAction<Partial<KanbanState['filters']>>) {
      state.filters = { ...state.filters, ...action.payload }
    },
    setKanbanFieldFilter(
      state,
      action: PayloadAction<{
        fieldId: number
        value: KanbanFieldFilterValue | null
      }>,
    ) {
      const key = String(action.payload.fieldId)
      const next = { ...(state.filters.fieldFilters ?? {}) }
      if (
        action.payload.value == null ||
        (Array.isArray(action.payload.value) &&
          action.payload.value.length === 0)
      ) {
        delete next[key]
      } else if (
        typeof action.payload.value === 'object' &&
        !Array.isArray(action.payload.value)
      ) {
        const from = action.payload.value.from?.trim() ?? ''
        const to = action.payload.value.to?.trim() ?? ''
        if (!from && !to) {
          delete next[key]
        } else {
          next[key] = {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          }
        }
      } else {
        next[key] = action.payload.value
      }

      if (Object.keys(next).length === 0) {
        delete state.filters.fieldFilters
      } else {
        state.filters.fieldFilters = next
      }
    },
    setKanbanFieldFilters(
      state,
      action: PayloadAction<Record<string, KanbanFieldFilterValue> | undefined>,
    ) {
      if (!action.payload || Object.keys(action.payload).length === 0) {
        delete state.filters.fieldFilters
      } else {
        state.filters.fieldFilters = action.payload
      }
    },
    clearKanbanFieldFilters(state) {
      delete state.filters.fieldFilters
    },

    /** Имя менеджера с crm-kanban/card — в списке get-all-managers его может не быть */
    upsertKanbanManagerFromCard(
      state,
      action: PayloadAction<{ id: number; displayName: string }>,
    ) {
      const { id, displayName } = action.payload
      if (!Number.isFinite(id) || id <= 0) return
      const prev = state.managersById[id]
      const fio = displayName.trim() || prev?.fio || `ID ${id}`
      state.managersById[id] = {
        id,
        fio,
        userId: prev?.userId ?? null,
      }
    },

    // Локальный патч из модалки лида
    cardPatched(
      state,
      action: PayloadAction<Partial<KanbanCard> & { leadFunnelId: number }>,
    ) {
      const id = action.payload.leadFunnelId
      if (!state.cardsById[id]) return
      state.cardsById[id] = { ...state.cardsById[id], ...action.payload }
    },

    // Удаление карточки из доски
    cardRemoved(state, action: PayloadAction<{ leadFunnelId: number }>) {
      const { leadFunnelId } = action.payload
      const card = state.cardsById[leadFunnelId]
      if (!card) return
      const col = state.columnsByStatusId[card.statusId]
      if (col) {
        col.ids = col.ids.filter((id) => id !== leadFunnelId)
        col.total = Math.max(0, col.total - 1)
      }
      delete state.cardsById[leadFunnelId]
    },

    // Движение карточки лида по статусам
    setLeadsByStatusDnd(
      state,
      action: PayloadAction<Record<string, string[]>>,
    ) {
      Object.entries(action.payload).forEach(([statusIdKey, ids]) => {
        const statusId = Number(statusIdKey)
        const column = state.columnsByStatusId[statusId]
        if (!column) return

        const nextIds = ids.map(Number)
        const prevLength = column.ids.length
        column.ids = nextIds
        column.total = Math.max(0, column.total + (nextIds.length - prevLength))

        nextIds.forEach((leadFunnelId) => {
          const card = state.cardsById[leadFunnelId]
          if (!card) return
          card.statusId = statusId
        })
      })
    },

    /** Новая карточка после insert-lead + getCard (только если ещё нет в cardsById) */
    prependKanbanCardFromSync(state, action: PayloadAction<KanbanCard>) {
      const card = action.payload
      if (card.funnelId !== state.activeFunnelId) return
      const column = state.columnsByStatusId[card.statusId]
      if (!column) return
      const lfId = card.leadFunnelId
      if (state.cardsById[lfId]) return

      state.cardsById[lfId] = card
      if (!column.ids.includes(lfId)) {
        column.total += 1
      }
      column.ids = [lfId, ...column.ids.filter((id) => id !== lfId)]
    },

    patchKanbanCardFunnelListRow(
      state,
      action: PayloadAction<{
        leadFunnelId: number
        funnelListRow: LeadsTableRow
      }>,
    ) {
      const card = state.cardsById[action.payload.leadFunnelId]
      if (card) {
        card.funnelListRow = action.payload.funnelListRow
      }
    },

    patchKanbanCardsVkUnreadByLeadId(
      state,
      action: PayloadAction<{
        leadId: number
        unreadCount: number
        updatedAt?: string | null
        provider?: CrmChatProviderKey
      }>,
    ) {
      const targetLeadId = Number(action.payload.leadId)
      if (!Number.isFinite(targetLeadId) || targetLeadId <= 0) return

      const {
        unreadCount,
        updatedAt,
        provider = 'vkontakte',
      } = action.payload

      for (const [key, card] of Object.entries(state.cardsById)) {
        if (Number(card.leadId) !== targetLeadId) continue
        state.cardsById[Number(key)] =
          buildPatchedKanbanCardProviderUnread(
            card,
            provider,
            unreadCount,
            updatedAt,
          )
      }
    },

    /** Оверлей на карточке (как при sync с модалкой): «Взять» → refetch доски и т.п. */
    kanbanCardPendingSet(
      state,
      action: PayloadAction<{ leadFunnelId: number }>,
    ) {
      const id = Number(action.payload.leadFunnelId)
      if (!Number.isFinite(id) || id <= 0) return
      state.cardSyncPendingByLeadFunnelId[id] = true
    },
    kanbanCardPendingClear(
      state,
      action: PayloadAction<{ leadFunnelId: number }>,
    ) {
      const id = Number(action.payload.leadFunnelId)
      if (!Number.isFinite(id) || id <= 0) return
      delete state.cardSyncPendingByLeadFunnelId[id]
    },

    setColumnSort(
      state,
      action: PayloadAction<{ statusId: StatusId; sort: Partial<KanbanColumnSort> }>,
    ) {
      const { statusId, sort } = action.payload
      const prev = state.columnSortByStatusId[statusId]
      if (!prev) return
      state.columnSortByStatusId[statusId] = { ...prev, ...sort }
    },

    setKanbanColumnsRefetchOverlay(
      state,
      action: PayloadAction<{ statusIds: StatusId[] }>,
    ) {
      for (const statusId of action.payload.statusIds) {
        const column = state.columnsByStatusId[statusId]
        if (column) {
          column.refetchOverlay = true
        }
      }
    },

    clearKanbanColumnsRefetchOverlay(
      state,
      action: PayloadAction<{ statusIds: StatusId[] }>,
    ) {
      for (const statusId of action.payload.statusIds) {
        const column = state.columnsByStatusId[statusId]
        if (column) {
          column.refetchOverlay = false
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Загрузка воронок-табов
      .addCase(fetchFunnelsThunk.pending, (state) => {
        state.loadingFunnels = true
        state.funnelsError = null
      })
      .addCase(fetchFunnelsThunk.fulfilled, (state, action) => {
        state.loadingFunnels = false
        state.funnels = action.payload ?? []
        state.funnelsError = null

        if (!state.activeFunnelId) {
          state.activeFunnelId = state.funnels[0]?.id ?? null
        }
      })
      .addCase(fetchFunnelsThunk.rejected, (state, action) => {
        state.loadingFunnels = false
        state.funnelsError =
          (action.payload as string) ?? 'Не удалось загрузить воронки'
      })

      // Загрузка менеджеров
      .addCase(fetchManagersThunk.pending, (state) => {
        state.loadingManagers = true
        state.managersError = null
      })
      .addCase(fetchManagersThunk.fulfilled, (state, action) => {
        state.loadingManagers = false
        state.managersError = null
        state.managersById = (action.payload ?? []).reduce<
          Record<number, { id: number; fio: string; userId: number | null }>
        >((acc, manager) => {
          acc[manager.id] = manager
          return acc
        }, {})
      })
      .addCase(fetchManagersThunk.rejected, (state, action) => {
        state.loadingManagers = false
        state.managersError =
          (action.payload as string) ?? 'Не удалось загрузить менеджеров'
      })

      // Загрузка источников
      .addCase(fetchSourcesThunk.pending, (state) => {
        state.loadingSources = true
        state.sourcesError = null
      })
      .addCase(fetchSourcesThunk.fulfilled, (state, action) => {
        state.loadingSources = false
        state.sourcesError = null
        state.sources = action.payload ?? []
      })
      .addCase(fetchSourcesThunk.rejected, (state, action) => {
        state.loadingSources = false
        state.sourcesError =
          (action.payload as string) ?? 'Не удалось загрузить источники'
      })

      // Загрузка доски
      .addCase(fetchBoardInitThunk.pending, (state) => {
        state.loadingBoard = true
        state.error = null
        state.cardsById = {}
        state.columnsByStatusId = {}
        state.columnOrder = []
      })
      .addCase(fetchBoardInitThunk.fulfilled, (state, action) => {
        state.loadingBoard = false
        if (!action.payload) {
          state.cardSyncPendingByLeadFunnelId = {}
          return
        }

        applyKanbanBoardPayload(state, action.payload)
      })
      .addCase(fetchBoardInitThunk.rejected, (state, action) => {
        state.loadingBoard = false
        state.error = action.payload as string
        state.cardSyncPendingByLeadFunnelId = {}
      })

      .addCase(refetchKanbanBoardThunk.pending, (state, action) => {
        const silent = action.meta.arg?.silent ?? true
        if (silent) return
        state.loadingBoard = true
        state.error = null
      })
      .addCase(refetchKanbanBoardThunk.fulfilled, (state, action) => {
        if (!action.payload) return
        const { silent, board } = action.payload
        if (!silent) {
          state.loadingBoard = false
        }
        applyKanbanBoardPayload(state, board)
      })
      .addCase(refetchKanbanBoardThunk.rejected, (state, action) => {
        const silent = action.meta.arg?.silent ?? true
        if (silent) return
        state.loadingBoard = false
        state.error = action.payload as string
      })

      .addCase(fetchFunnelUnassignedCountsThunk.fulfilled, (state, action) => {
        applyFunnelUnassignedCounts(state, action.payload ?? [])
      })

      // Догрузка карточек колонки
      .addCase(fetchColumnMoreThunk.pending, (state, action) => {
        const statusId = action.meta.arg
        const column = state.columnsByStatusId[statusId]
        if (!column) return
        column.loadingMore = true
        column.error = null
      })
      .addCase(fetchColumnMoreThunk.fulfilled, (state, action) => {
        const statusId = action.payload?.statusId ?? action.meta.arg
        const column = state.columnsByStatusId[statusId]
        if (!column) return

        if (!action.payload) {
          column.loadingMore = false
          column.error = null
          return
        }

        const nextIds: number[] = []
        action.payload.items.forEach((card) => {
          state.cardsById[card.leadFunnelId] = card
          nextIds.push(card.leadFunnelId)
        })

        const uniqueAppendedIds = nextIds.filter((id) => !column.ids.includes(id))
        column.ids = [...column.ids, ...uniqueAppendedIds]
        column.cursor = action.payload.nextCursor
        column.hasMore = action.payload.hasMore
        column.total = action.payload.total
        column.loadingMore = false
        column.error = null
      })
      .addCase(fetchColumnMoreThunk.rejected, (state, action) => {
        const statusId = action.meta.arg
        const column = state.columnsByStatusId[statusId]
        if (!column) return
        column.loadingMore = false
        column.error =
          (action.payload as string) ?? 'Не удалось догрузить колонку'
      })

      .addCase(refetchKanbanColumnThunk.pending, (state, action) => {
        const { statusId, silent } = parseRefetchKanbanColumnArg(action.meta.arg)
        const column = state.columnsByStatusId[statusId]
        if (!column) return
        if (!silent) {
          column.refetchOverlay = true
        }
        column.error = null
      })
      .addCase(refetchKanbanColumnThunk.fulfilled, (state, action) => {
        const silent = Boolean(action.payload?.silent)
        if (!action.payload) return
        const { statusId, items, nextCursor, hasMore, total } = action.payload
        const column = state.columnsByStatusId[statusId]
        if (!column) return

        const nextIds: number[] = []
        items.forEach((card) => {
          state.cardsById[card.leadFunnelId] = card
          nextIds.push(card.leadFunnelId)
        })
        column.ids = nextIds
        column.cursor = nextCursor
        column.hasMore = hasMore
        column.total = total
        column.error = null
        if (!silent) {
          column.refetchOverlay = false
        }
      })
      .addCase(refetchKanbanColumnThunk.rejected, (state, action) => {
        const { statusId, silent } = parseRefetchKanbanColumnArg(action.meta.arg)
        const column = state.columnsByStatusId[statusId]
        if (!column) return
        if (!silent) {
          column.refetchOverlay = false
        }
        column.error =
          (action.payload as string) ?? 'Не удалось обновить колонку'
      })

      .addCase(refreshKanbanColumnsAfterLeadChangeThunk.pending, (state, action) => {
        const id = Number(action.meta.arg)
        if (!Number.isFinite(id) || id <= 0) return
        state.cardSyncPendingByLeadFunnelId[id] = true
      })
      .addCase(refreshKanbanColumnsAfterLeadChangeThunk.fulfilled, (state, action) => {
        const id = Number(action.meta.arg)
        if (!Number.isFinite(id) || id <= 0) return
        delete state.cardSyncPendingByLeadFunnelId[id]
      })
      .addCase(refreshKanbanColumnsAfterLeadChangeThunk.rejected, (state, action) => {
        const id = Number(action.meta.arg)
        if (!Number.isFinite(id) || id <= 0) return
        delete state.cardSyncPendingByLeadFunnelId[id]
      })
  },
})

export const {
  setActiveFunnelId,
  setFilters,
  setKanbanFieldFilter,
  setKanbanFieldFilters,
  clearKanbanFieldFilters,
  upsertKanbanManagerFromCard,
  cardPatched,
  cardRemoved,
  setLeadsByStatusDnd,
  prependKanbanCardFromSync,
  patchKanbanCardFunnelListRow,
  patchKanbanCardsVkUnreadByLeadId,
  kanbanCardPendingSet,
  kanbanCardPendingClear,
  setColumnSort,
  setKanbanColumnsRefetchOverlay,
  clearKanbanColumnsRefetchOverlay,
} = kanbanSlice.actions
export default kanbanSlice.reducer
